<template>
  <div class="broker-login-page">
    <!-- Language selector at top-right -->
    <div class="language-selector-container">
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <button v-bind="attrs" v-on="on" class="language-pill" elevation="0">
            <Flag :code="currentLanguageFlag" :squared="false" class="flag" />
            <span class="lang-code">{{ currentLanguage.toUpperCase() }}</span>
            <v-icon class="chevron" color="white" size="16"
              >fa-regular fa-chevron-down</v-icon
            >
          </button>
        </template>
        <v-list dense class="language-list">
          <v-list-item
            v-for="lang in availableLanguages"
            :key="lang.code"
            @click="changeLanguage(lang.code)"
          >
            <v-list-item-content>
              <v-list-item-title class="language-item-title">
                <Flag :code="lang.flag" :squared="false" class="mr-2" />
                {{ lang.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action v-if="currentLanguage === lang.code">
              <v-icon color="primary" small>fa-regular fa-check</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="login-grid">
      <div class="left-card">
        <div class="head-left">
          <div class="left-header">
            <img
              class="brand-logo"
              src="@/assets/brokers/Logo-swiset.png"
              alt="SWISET"
            />
            <div class="brand-sep">—</div>
            <div class="brand-title">
              {{ $t("broker.login.brokerAdmin") }}
            </div>
            <div class="container-stepper">
              <Stepper
                :steps="steps"
                :activeStep="currentStep"
                :setCurrentStep="setCurrentStep"
                :maxStep="maxStep"
              >
              </Stepper>
            </div>
          </div>
          <div class="signup__header">
            <h2 class="signup__title">
              {{ $t("broker.login.newUsers.welcome") }}
            </h2>
            <p class="signup__subtitle">
              {{ $t("broker.login.newUsers.invitedToJoin") }}
              <b>{{ brokerName }}</b
              >.<br />
              {{ $t("broker.login.newUsers.completeProfileDesc") }}
            </p>
            <!-- Email -->
            <div class="field-block">
              <div class="field-label">
                {{ $t("broker.login.newUsers.emailAddress") }}
              </div>
              <div class="field-email-display">
                {{ signupPayload.email || "name@broker.com" }}
              </div>
              <!-- <v-text-field
                v-model="signupPayload.email"
                class="field-email"
                placeholder="name@broker.com"
                outlined
                dense
                readonly
                hide-details
              /> -->
            </div>
          </div>
        </div>
        <div class="form-content">
          <sign-up-new-user-one
            ref="step1Form"
            v-show="currentStep === 1"
            :invitedEmail="signupPayload.email"
            :brokerName="brokerName"
            @next="onStep1Next"
          ></sign-up-new-user-one>
          <sign-up-new-user-two
            v-show="currentStep === 2"
            :invitedEmail="signupPayload.email"
            :loading="loading"
            @back="onStep2Back"
            @submit="onStep2Submit"
          ></sign-up-new-user-two>
        </div>
      </div>

      <!-- Right Panel - Image/Branding -->

      <div class="right-content">
        <div class="right-inner">
          <v-carousel
            v-model="activeSlide"
            class="awards-carousel"
            height="240"
            cycle
            interval="3000"
            :show-arrows="false"
            hide-delimiters
            hide-delimiter-background
          >
            <v-carousel-item v-for="(s, i) in slides" :key="i">
              <div class="triple-slide">
                <!-- left preview -->
                <img
                  class="slide-img slide-img--side"
                  :src="slides[prevIndex(i)].src"
                  :alt="slides[prevIndex(i)].alt"
                />

                <!-- center main -->
                <img
                  class="slide-img slide-img--main"
                  :src="slides[i].src"
                  :alt="slides[i].alt"
                />

                <!-- right preview -->
                <img
                  class="slide-img slide-img--side"
                  :src="slides[nextIndex(i)].src"
                  :alt="slides[nextIndex(i)].alt"
                />
              </div>
            </v-carousel-item>
          </v-carousel>
          <p class="text-broker">
            {{ $t("broker.login.message") }}
          </p>
        </div>
        <div class="right-watermark">
          <img src="@/assets/brokers/Logo-swiset.png" alt="SWISET" />
        </div>
      </div>
    </div>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" :timeout="5000" color="error" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <!-- Success confirmation dialog (after signup) -->
    <v-dialog
      v-model="showSuccessDialog"
      max-width="400"
      persistent
      content-class="signup-success-dialog"
    >
      <v-card>
        <v-card-title class="text-h6 signup-success-dialog__title">
          {{ $t("broker.login.newUsers.signupSuccessTitle") }}
        </v-card-title>
        <v-card-text>
          {{ $t("broker.login.newUsers.signupSuccessMessage") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="goToLogin">
            {{ $t("broker.login.newUsers.signupSuccessOk") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getAvailableLocales, setLocale, getLocale } from "../i18n";
import SignUpNewUserOne from "./Log/SignUpNewUserOne.vue";
import SignUpNewUserTwo from "./Log/SignUpNewUserTwo.vue";
import Stepper from "@/views/Funding/Simulation/components/Wizard/Stepper.vue";
import membersService from "../services/members.service";

export default {
  components: { SignUpNewUserOne, SignUpNewUserTwo, Stepper },
  name: "BrokerAuthShell",
  data() {
    return {
      steps: 2,
      currentStep: 1,
      maxStep: 1,
      brokerName: "",
      accessKey: "",
      signupPayload: {
        email: "",
        firstName: "",
        lastName: "",
        country: null,
        photoFile: null,
      },
      loginForm: null,
      loading: false,
      formValid: false,
      showPassword: false,
      rememberMe: false,
      showError: false,
      errorMessage: "",
      showSuccessDialog: false,
      currentLanguage: getLocale(),
      availableLanguages: getAvailableLocales(),
      activeSlide: 0,
      credentials: {
        username: "", // Pre-filled for testing, remove in production
        password: "", // Pre-filled for testing, remove in production
      },
      usernameRules: [
        (v) => !!v || "Email address is required",
        (v) => v.length >= 3 || "Username must be at least 3 characters",
      ],
      passwordRules: [
        (v) => !!v || "Password is required",
        (v) => v.length >= 6 || "Password must be at least 6 characters",
      ],
      slides: [
        {
          src: require("@/assets/brokers/Award-Guinness-World-Record.png"),
          alt: "Prize 1",
        },
        {
          src: require("@/assets/brokers/Award-Best-Fintech-Solution.png"),
          alt: "Prize 2",
        },
        {
          src: require("@/assets/brokers/Award-Best-Trading-Competition-Provider.png"),
          alt: "Prize 3",
        },
        {
          src: require("@/assets/brokers/Award-Best-Trading-Tools.png"),
          alt: "Prize 4",
        },
        {
          src: require("@/assets/brokers/Award-Top-100-Global-Startups.png"),
          alt: "Prize 4",
        },
      ],
    };
  },

  mounted() {
    const emailFromQuery = this.$route?.query?.email;
    if (emailFromQuery && typeof emailFromQuery === "string") {
      this.signupPayload.email = emailFromQuery.trim();
    }
    const partnerFromQuery = this.$route?.query?.partner;
    if (partnerFromQuery && typeof partnerFromQuery === "string") {
      this.brokerName = partnerFromQuery.trim();
    }
    const accessKeyFromQuery = this.$route?.query?.accessKey;
    if (accessKeyFromQuery && typeof accessKeyFromQuery === "string") {
      this.accessKey = accessKeyFromQuery.trim();
    }
  },
  computed: {
    currentLanguageFlag() {
      const lang = this.availableLanguages.find(
        (l) => l.code === this.currentLanguage,
      );
      return lang ? lang.flag : "US";
    },
  },

  methods: {
    prevIndex(i) {
      return (i - 1 + this.slides.length) % this.slides.length;
    },
    nextIndex(i) {
      return (i + 1) % this.slides.length;
    },

    setCurrentStep(step) {
      if (step < 1 || step > this.maxStep) return;
      // When trying to go from step 1 to step 2, require mandatory step 1 fields (use current form state from child)
      if (step === 2 && this.currentStep === 1) {
        const step1 = this.$refs.step1Form;
        if (!step1 || !step1.isStep1FormValid()) return;
      }
      this.currentStep = step;
    },

    // Step 1 -> Next
    onStep1Next(payload) {
      const prevEmail = this.signupPayload.email;
      this.signupPayload = { ...this.signupPayload, ...payload };
      // Keep email from URL/parent if payload didn't provide it (step 1 has no email field)
      if (prevEmail && !this.signupPayload.email) {
        this.signupPayload.email = prevEmail;
      }

      this.maxStep = Math.max(this.maxStep, 2);
      this.currentStep = 2;
    },

    // Step 2 -> Back
    onStep2Back() {
      this.currentStep = 1;
    },

    goToLogin() {
      this.showSuccessDialog = false;
      this.$router
        .push({ name: "broker-login", query: this.$route.query })
        .catch(() => {});
    },

    // Step 2 -> Submit final (signup-from-invite)
    async onStep2Submit(payload) {
      const finalPayload = { ...this.signupPayload, ...payload };
      // Email comes from parent (URL / disabled field); do not use payload email if it would overwrite with empty
      const email =
        (this.signupPayload.email && this.signupPayload.email.trim()) ||
        (finalPayload.email && String(finalPayload.email).trim()) ||
        "";
      if (!this.accessKey) {
        this.errorMessage =
          this.$t("broker.login.newUsers.missingAccessKey") ||
          "Missing access key.";
        this.showError = true;
        return;
      }
      const countryStr =
        typeof finalPayload.country === "string"
          ? finalPayload.country
          : (finalPayload.country && finalPayload.country.name) || "";
      this.loading = true;
      this.showError = false;
      try {
        await membersService.signupFromInvite(
          {
            accessKey: this.accessKey,
            email,
            firstName: finalPayload.firstName || "",
            lastName: finalPayload.lastName || "",
            country: countryStr,
            phoneNumber: finalPayload.phoneNumber || "",
            username: finalPayload.username || "",
            password: finalPayload.password || "",
          },
          finalPayload.photoFile || null,
        );
        this.errorMessage = "";
        this.showSuccessDialog = true;
      } catch (err) {
        let msg =
          (err.response &&
            err.response.data &&
            (err.response.data.message || err.response.data.error)) ||
          err.message ||
          "Signup failed.";
        if (err.response && err.response.status === 409) {
          msg =
            this.$t("broker.login.newUsers.usernameAlreadyExists") ||
            "This username is already taken. Please choose another.";
        }
        this.errorMessage = msg;
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
    changeLanguage(code) {
      setLocale(code);
      this.currentLanguage = code;
    },
  },
};
</script>

<style lang="scss" scoped>
.broker-login-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background-image: url("~@/assets/brokers/Background-Login.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.login-grid {
  width: 100%;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 0;
  align-items: stretch;
}

.left-card {
  height: 100%;
  padding: 30px 30px;
  background: #fff !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22) !important;
  border-top-right-radius: 1.5rem;
  border-bottom-right-radius: 1.5rem;
}

.left-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10%;
}

.brand-logo {
  height: 26px;
  width: auto;
}
.brand-sep {
  color: #005931 !important;
}

.brand-title {
  font-family: "Aeonik", sans-serif !important;
  font-size: 26px;
  font-weight: 400;
  text-transform: capitalize;
  color: #005931 !important;
}
.container-stepper .stepper {
  width: auto;
  gap: 8px;
}

.signup__header {
  margin-bottom: 18px;
}

.signup__title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #005931 !important;
}

.signup__subtitle {
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.6);
}

.field-block {
  margin-bottom: 14px;
}

.field-label {
  display: block;
  font-size: 12px;
  color: #3b3b3b;
  margin-bottom: 2px;
}

.field-email-display {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  // background: rgba(0,0,0,0.03);
  font-size: 14px;
  color: rgba(0, 0, 0, 0.38);
}

.form-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

// contenido pane derecho
.right-content {
  position: relative;
  height: 100%;
  padding: 44px 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.awards-carousel {
  width: 100%;
  max-width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px; /* más compacto */
  overflow: hidden; /* evita que se desborde */
}

/* image layout */
.triple-slide {
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.slide-img {
  object-fit: contain;
  filter: brightness(0) invert(1);
  object-fit: contain;
  max-width: 100%;
  transition: all 0.6s ease;
}

.slide-img--main {
  width: 55%;
  max-height: 180px;
  opacity: 0.98;
  transform: scale(1);
}

.slide-img--side {
  width: 20%;
  max-height: 120px;
  opacity: 0.45;
  transform: scale(0.95);
}

.text-broker {
  margin-top: 12px;
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
  color: rgba(255, 255, 255, 0.85) !important;
}

.right-watermark {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
}

.right-watermark img {
  width: 100%;
  height: auto;
  object-fit: contain;
  opacity: 0.9;
}

.right-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: center;
}

// Lenguage selector

.language-selector-container {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
}

.language-pill {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 4px 10px !important;
  border-radius: 10px !important;
  border: 2px solid rgba(255, 255, 255, 0.55) !important;
  background: rgba(0, 0, 0, 0.12) !important;
  color: rgba(255, 255, 255, 0.85) !important;
  text-transform: none !important;
  min-width: 130px;

  &::before,
  &::after {
    display: none !important;
  }
}

.flag {
  width: 44px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
}

.lang-code {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.chevron {
  margin-left: auto;
  opacity: 0.85;
}

.language-list {
  min-width: 220px;
}

.language-item-title {
  display: flex;
  align-items: center;
}

/* Responsive: en móvil solo card */
@media (max-width: 960px) {
  .login-grid {
    grid-template-columns: 1fr;
  }

  .right-content {
    display: none !important;
  }

  .left-card {
    width: 100%;
    border-radius: 0; /* opcional: en mobile suele verse mejor */
  }

  .broker-login-page {
    min-height: 100vh;
    min-height: 100dvh; /* mejor en mobile */
  }

  .form-content {
    width: 100%;
  }

  .brand-title {
    font-size: 16px;
  }

  .language-selector-container {
    top: 16px;
    right: 16px;
  }
}
</style>
